import React, { FC } from 'react'
import { Controller, ControllerProps } from 'react-hook-form'
import AutocompleteShipper, {
  IAutocompleteShipperProps,
} from 'transfix-ui/components/AutocompleteShipper'

type IBaseControllerProps = ControllerProps<React.ComponentType<IAutocompleteShipperProps>>

export interface IControlledAutocompleteShipperProps
  extends Pick<IBaseControllerProps, 'control' | 'name' | 'defaultValue'> {
  AutocompleteShipperProps?: Omit<IAutocompleteShipperProps, 'value' | 'onChange'> &
    Partial<Pick<IAutocompleteShipperProps, 'onChange'>>
}

/**
 * Helper component for use with React Hook Form
 * @see https://react-hook-form.com/api#Controller
 * @example
 * ```
 * <ControlledAutocompleteShipper
 *   name='Shipper'
 *   control={control}
 * />
 * ```
 */
const ControlledAutocompleteShipper: FC<IControlledAutocompleteShipperProps> = ({
  AutocompleteShipperProps = {},
  control,
  defaultValue,
  name,
}) => {
  const { onChange: onChangeProp, ...rest } = AutocompleteShipperProps || {}
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ onBlur, onChange, value }) => {
        const changeHandler: IAutocompleteShipperProps['onChange'] = newVal => {
          onChange(newVal)

          /**
           * Only used for triggering "onChange" events, like a search submit
           */
          if (onChangeProp) {
            onChangeProp(newVal)
          }
        }

        return (
          <AutocompleteShipper onChange={changeHandler} value={value} onBlur={onBlur} {...rest} />
        )
      }}
    />
  )
}

export default ControlledAutocompleteShipper
